import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Store, ChevronDown, Check, CalendarRange, Sun, Moon, Menu, LogOut, KeyRound, UserRound, Layers } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { FeedPill } from "@/components/dashboard/FeedAlertBanner";
import { Brand, NavItems } from "@/components/shell/Sidebar";
import { useScope, PERIOD_TABS, PERIOD_PRESETS } from "@/lib/scope";
import { useTheme } from "@/lib/theme";
import { api, errorMessage } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { cn } from "@/lib/utils";

const iso = (d) => (d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : null);
const fromIso = (s) => (s ? new Date(`${s}T00:00:00`) : undefined);

/* ------------------------------------------------------------------ store scope picker */
export const StorePicker = ({ scopes, clusters }) => {
  const { scope, setScope } = useScope();
  const [open, setOpen] = useState(false);
  const current = scopes?.find((s) => s.id === scope);
  const byCluster = useMemo(() => {
    const groups = {};
    (scopes || []).filter((s) => s.kind === "site").forEach((s) => {
      (groups[s.cluster] = groups[s.cluster] || []).push(s);
    });
    return groups;
  }, [scopes]);
  const pick = (id) => {
    setScope(id);
    setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" data-testid="scope-bar-store-picker" className="h-9 gap-2 max-w-[260px] justify-between bg-card/70 hover:bg-muted/40 focus-gold">
          <span className="flex items-center gap-2 min-w-0">
            {current?.kind === "site" ? <Store size={15} className="text-gold shrink-0" /> : <Layers size={15} className="text-gold shrink-0" />}
            <span className="truncate text-sm" data-testid="scope-bar-store-label">{current?.name || "All stores"}</span>
          </span>
          <ChevronDown size={14} className="text-muted-foreground shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-[320px] bg-popover/95 backdrop-blur-xl" data-testid="scope-bar-store-menu">
        <Command>
          <CommandInput placeholder="Search stores or malls" data-testid="scope-bar-store-search" />
          <CommandList className="max-h-[360px] scroll-thin">
            <CommandEmpty>No store found.</CommandEmpty>
            <CommandGroup heading="Group">
              {(scopes || []).filter((s) => s.kind !== "site").map((s) => (
                <CommandItem key={s.id} value={`${s.name} ${s.id}`} onSelect={() => pick(s.id)} data-testid={`scope-option-${s.id.replace(":", "-")}`} className="gap-2">
                  <Layers size={14} className={cn(s.kind === "all" ? "text-gold" : "text-muted-foreground")} />
                  <span className="flex-1">
                    <span className="text-sm">{s.name}</span>
                    <span className="block text-[11px] text-muted-foreground">{s.stores} store{s.stores === 1 ? "" : "s"}{!s.has_data ? " · awaiting data" : ""}</span>
                  </span>
                  {scope === s.id && <Check size={14} className="text-gold" />}
                </CommandItem>
              ))}
            </CommandGroup>
            {(clusters || []).map((c) => (
              <CommandGroup key={c.id} heading={c.name}>
                {(byCluster[c.id] || []).map((s) => (
                  <CommandItem key={s.id} value={`${s.name} ${c.name} ${s.id}`} onSelect={() => pick(s.id)} data-testid={`scope-option-${s.id}`} className="gap-2">
                    <Store size={14} className={cn(s.has_data ? "text-muted-foreground" : "text-muted-foreground/40")} />
                    <span className="flex-1">
                      <span className={cn("text-sm", !s.has_data && "text-muted-foreground")}>{s.name}</span>
                      <span className="block text-[11px] text-muted-foreground">{s.category_label}{!s.has_data ? " · awaiting data" : ""}</span>
                    </span>
                    {scope === s.id && <Check size={14} className="text-gold" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

/* ------------------------------------------------------------------ period picker */
export const PeriodPicker = ({ anchor }) => {
  const { period, setPeriod, range, setCustomRange } = useScope();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => ({ from: fromIso(range?.start), to: fromIso(range?.end) }));
  const tab = period === "custom" ? "custom" : period.startsWith("last_") || period === "mom" || period === "last_month" ? (period === "mom" || period === "last_month" ? "mom" : "wow") : period === "wow" ? "wow" : "custom";
  const apply = () => {
    if (draft.from && draft.to) {
      setCustomRange(iso(draft.from), iso(draft.to));
      setOpen(false);
    } else toast.error("Pick a start and an end date");
  };
  return (
    <div className="flex items-center gap-2">
      <Tabs value={tab} onValueChange={(v) => (v === "custom" ? setOpen(true) : setPeriod(v))}>
        <TabsList className="h-9 bg-card/70" data-testid="scope-bar-period-tabs">
          {PERIOD_TABS.map((t) => (
            <TabsTrigger key={t.id} value={t.id} data-testid={`period-tab-${t.id}`} className="text-xs px-3 data-[state=active]:bg-gold/15 data-[state=active]:text-foreground">
              <span className="hidden md:inline">{t.label}</span>
              <span className="md:hidden">{t.short}</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" data-testid="scope-bar-date-range-button" className="h-9 gap-2 bg-card/70 hover:bg-muted/40 font-mono text-xs">
            <CalendarRange size={14} className="text-gold" />
            <span className="hidden sm:inline" data-testid="scope-bar-period-label">
              {period === "custom" && range ? `${fmtDate(range.start)} – ${fmtDate(range.end)}` : PERIOD_PRESETS.find((p) => p.id === period)?.label || period}
            </span>
            <ChevronDown size={13} className="text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-auto p-0 bg-popover/95 backdrop-blur-xl" data-testid="scope-bar-period-menu">
          <div className="flex flex-col md:flex-row">
            <div className="p-2 border-b md:border-b-0 md:border-r border-border/60 min-w-[200px]">
              <div className="eyebrow px-2 py-1.5">Presets</div>
              {PERIOD_PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`period-preset-${p.id}`}
                  onClick={() => {
                    setPeriod(p.id);
                    setOpen(false);
                  }}
                  className={cn("w-full text-left text-sm rounded-md px-2 py-1.5 hover:bg-muted/40 focus-gold flex items-center justify-between", period === p.id && "bg-gold/12 text-foreground")}
                >
                  {p.label}
                  {period === p.id && <Check size={13} className="text-gold" />}
                </button>
              ))}
            </div>
            <div className="p-3">
              <div className="eyebrow mb-1">Custom range</div>
              <Calendar mode="range" numberOfMonths={1} selected={draft} onSelect={(r) => setDraft(r || {})} defaultMonth={draft.from || fromIso(anchor)} data-testid="scope-bar-calendar" />
              <div className="flex items-center justify-between gap-2 mt-2">
                <span className="font-mono text-[11px] text-muted-foreground">{draft.from ? fmtDate(iso(draft.from)) : "start"} → {draft.to ? fmtDate(iso(draft.to)) : "end"}</span>
                <Button size="sm" className="h-8" data-testid="scope-bar-apply-range" onClick={apply}>Apply</Button>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

/* ------------------------------------------------------------------ theme + overlay toggles */
export const ThemeToggle = ({ className }) => {
  const { theme, toggle } = useTheme();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="outline" size="icon" className={cn("h-9 w-9 bg-card/70 hover:bg-muted/40", className)} onClick={toggle} data-testid="theme-toggle" aria-label="Toggle light / dark theme">
          {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs">Switch to {theme === "dark" ? "light" : "dark"} theme</TooltipContent>
    </Tooltip>
  );
};

const OverlayToggle = () => {
  const { overlay, setOverlay } = useScope();
  return (
    <div className="hidden xl:flex items-center gap-2 pl-2">
      <Switch id="overlay" checked={overlay} onCheckedChange={setOverlay} data-testid="holiday-overlay-toggle" className="data-[state=checked]:bg-gold" />
      <Label htmlFor="overlay" className="text-xs text-muted-foreground cursor-pointer">Holidays on charts</Label>
    </div>
  );
};

/* ------------------------------------------------------------------ user menu + change password */
export const UserMenu = ({ user, onLogout }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 8) return toast.error("New password must be at least 8 characters");
    if (form.next !== form.confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.changePassword(form.current, form.next);
      toast.success("Password updated");
      setOpen(false);
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(errorMessage(err, "Could not change password"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="h-9 gap-2 bg-card/70 hover:bg-muted/40 px-2.5" data-testid="scope-bar-user-menu">
            <span className="h-6 w-6 rounded-full bg-gold/20 text-gold flex items-center justify-center"><UserRound size={13} /></span>
            <span className="hidden md:inline text-sm max-w-[120px] truncate">{user?.name || user?.login}</span>
            <ChevronDown size={13} className="text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 bg-popover/95 backdrop-blur-xl">
          <DropdownMenuLabel className="font-normal">
            <div className="text-sm font-medium" data-testid="user-menu-name">{user?.name || user?.login}</div>
            <div className="text-[11px] text-muted-foreground">{user?.login} · <span className="uppercase tracking-wider" data-testid="user-menu-role">{user?.role}</span></div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setOpen(true)} data-testid="user-menu-change-password" className="gap-2"><KeyRound size={14} /> Change password</DropdownMenuItem>
          <DropdownMenuItem onClick={onLogout} data-testid="user-menu-logout" className="gap-2"><LogOut size={14} /> Sign out</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Change password</DialogTitle>
            <DialogDescription>Use at least 8 characters. You stay signed in after the change.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1.5"><Label htmlFor="cur">Current password</Label><Input id="cur" type="password" autoComplete="current-password" value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} data-testid="change-password-current" required /></div>
            <div className="space-y-1.5"><Label htmlFor="nxt">New password</Label><Input id="nxt" type="password" autoComplete="new-password" value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })} data-testid="change-password-new" required /></div>
            <div className="space-y-1.5"><Label htmlFor="cnf">Confirm new password</Label><Input id="cnf" type="password" autoComplete="new-password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="change-password-confirm" required /></div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="change-password-submit">{busy ? "Saving…" : "Update password"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

/* ------------------------------------------------------------------ the bar */
const LockedScope = () => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span data-testid="scope-bar-store-locked" className="h-9 inline-flex items-center gap-2 rounded-md border border-border/70 bg-card/70 px-3 text-sm shrink-0 cursor-default">
        <Layers size={15} className="text-gold" />
        All stores
        <span className="text-[11px] text-muted-foreground hidden md:inline">· group view</span>
      </span>
    </TooltipTrigger>
    <TooltipContent side="bottom" className="max-w-[260px] text-xs leading-relaxed">The executive summary always covers all 8 stores. Pick a store or mall on Count Analytics, Sales & POS or AI Growth.</TooltipContent>
  </Tooltip>
);

export const ScopeBar = ({ user, onLogout, scopes, clusters, anchor, feed, isAdmin, showScope = true, lockScope = false, hidePeriod = false, title }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  return (
    <header className="print-hidden sticky top-0 z-30 border-b border-border/60 bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/70" data-testid="scope-bar">
      <div className="px-4 sm:px-6 lg:px-8 min-h-16 py-2.5 md:py-0 md:h-16 flex flex-wrap md:flex-nowrap items-center gap-x-3 gap-y-2">
        <Sheet open={navOpen} onOpenChange={setNavOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" className="lg:hidden h-9 w-9 bg-card/70" data-testid="mobile-nav-open" aria-label="Open navigation"><Menu size={16} /></Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[300px] p-0 bg-[hsl(var(--sidebar-bg))] flex flex-col" data-testid="mobile-nav">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <div className="px-6 pt-6 pb-5"><Brand /></div>
            <NavItems isAdmin={isAdmin} feed={feed} onNavigate={() => setNavOpen(false)} />
            <div className="px-6 py-4 border-t border-border/60 text-[11px] text-muted-foreground">{location.pathname}</div>
          </SheetContent>
        </Sheet>
        <div className="lg:hidden"><Brand compact /></div>
        {showScope ? (
          <div className="order-last md:order-none basis-full md:basis-auto md:flex-1 min-w-0 flex items-center gap-2 overflow-x-auto scroll-thin py-1" data-testid="scope-bar-controls">
            {lockScope ? <LockedScope /> : <StorePicker scopes={scopes} clusters={clusters} />}
            {!hidePeriod && <PeriodPicker anchor={anchor} />}
            <OverlayToggle />
          </div>
        ) : (
          <div className="flex-1 min-w-0 font-display text-base truncate" data-testid="scope-bar-title">{title}</div>
        )}
        <div className={cn("flex items-center gap-2 shrink-0", showScope && "ml-auto md:ml-0")}>
          <div className="hidden sm:block"><FeedPill feed={feed} onNavigate={isAdmin ? () => navigate("/admin/live") : undefined} /></div>
          <ThemeToggle />
          <UserMenu user={user} onLogout={onLogout} />
        </div>
      </div>
    </header>
  );
};
